import { useState } from 'react';
import { z } from 'zod';

import { workerCreateSchema } from '@fin/shared/schemas';
import { SERVICE_TYPES, SERVICE_LABELS, EMPLOYMENT_TYPES } from '@fin/shared/enums';
import { BRAND } from '@fin/shared/brand';

import { Button, Card, Field, Modal, PageHeader, inputClass, useDisclosure } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';
import {
  useCreateWorker,
  usePatchWorker,
  useResendWorkerInvite,
  useWorkers,
  type Worker,
} from '../lib/queries';
import { Link } from 'react-router-dom';

export function WorkersPage() {
  const list = useWorkers();
  const patch = usePatchWorker();
  const resend = useResendWorkerInvite();
  const modal = useDisclosure();
  return (
    <>
      <PageHeader
        title="Workers"
        subtitle="Your team, services & live presence"
        icon={PageIcon.workers}
        accent={PageAccent.workers}
        action={<Button variant="white" onClick={modal.onOpen}>+ Add worker</Button>}
      />
      {list.isPending && <p className="text-stone-500">Loading…</p>}
      {list.isError && <p style={{ color: BRAND.danger }}>Failed to load: {(list.error as Error).message}</p>}
      {list.data && (
        <Card>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Services</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">Presence</th>
                <th className="px-4 py-3">Invite</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((w: Worker) => {
                const presence = derivePresence(w);
                return (
                <tr key={w.id} className="border-b border-stone-100 last:border-0">
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/workers/${w.id}`} className="hover:underline" style={{ color: BRAND.primary }}>
                      {w.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-stone-600">{w.phone}</td>
                  <td className="px-4 py-3 text-stone-600">{w.employmentType.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-stone-600">
                    {w.services.map((sv) => SERVICE_LABELS[sv as keyof typeof SERVICE_LABELS]?.en ?? sv).join(', ')}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => patch.mutate({ id: w.id, patch: { active: !w.active } })}
                      disabled={patch.isPending}
                      className="rounded-full px-2 py-0.5 text-xs hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: w.active ? '#DCFCE7' : '#FEE2E2',
                        color: w.active ? BRAND.success : BRAND.danger,
                      }}
                      title="Click to toggle"
                    >
                      {w.active ? 'active' : 'inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs"
                      style={{ backgroundColor: presence.bg, color: presence.fg }}
                      title={presence.title}
                    >
                      {presence.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {presence.kind === 'invited' && (
                      <button
                        onClick={() => {
                          if (!window.confirm(`Resend invite email with a new temporary password to ${w.email}?`)) return;
                          resend.mutate(w.id, {
                            onSuccess: () => alert(`Invite re-sent to ${w.email}.`),
                            onError: (err) => alert(`Failed: ${(err as Error).message}`),
                          });
                        }}
                        disabled={resend.isPending}
                        className="rounded border px-2 py-1 text-xs disabled:opacity-50"
                        style={{ borderColor: BRAND.primary, color: BRAND.primary, backgroundColor: 'white' }}
                      >
                        {resend.isPending ? 'Sending…' : 'Resend email'}
                      </button>
                    )}
                    {presence.kind !== 'invited' && (
                      <span className="text-xs text-stone-400">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
              {list.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-stone-500">
                    No workers yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>
      )}
      <Modal open={modal.open} onClose={modal.onClose} title="Add worker">
        <CreateWorkerForm onClose={modal.onClose} />
      </Modal>
    </>
  );
}

interface Presence {
  kind: 'online' | 'offline' | 'invited' | 'no_account' | 'never_seen';
  label: string;
  title: string;
  bg: string;
  fg: string;
}

/**
 * Combines Cognito UserStatus with the worker's last API-call timestamp:
 *  - "Invited" → never finished signup; admin can resend.
 *  - "Online" → last seen <5 min ago.
 *  - "5m ago" / "2h ago" / "3d ago" → relative offline.
 *  - "Never used" → confirmed account but no API call yet.
 */
function derivePresence(w: Worker): Presence {
  if (w.loginStatus === 'invited') {
    return {
      kind: 'invited',
      label: 'Invited',
      title: 'Invite email sent. Worker has not signed in yet.',
      bg: '#FEF3C7',
      fg: '#92400E',
    };
  }
  if (w.loginStatus === 'no_account') {
    return {
      kind: 'no_account',
      label: 'No account',
      title: 'No Cognito user found for this email.',
      bg: '#FEE2E2',
      fg: '#B91C1C',
    };
  }
  if (!w.lastSeenAt) {
    return {
      kind: 'never_seen',
      label: 'Never used',
      title: 'Worker has set a password but has not opened the app yet.',
      bg: '#F1F5F9',
      fg: '#475569',
    };
  }
  const ageMs = Date.now() - new Date(w.lastSeenAt).getTime();
  if (ageMs < 5 * 60_000) {
    return {
      kind: 'online',
      label: 'Online',
      title: `Last seen ${formatAgo(ageMs)}.`,
      bg: '#DCFCE7',
      fg: '#15803D',
    };
  }
  return {
    kind: 'offline',
    label: formatAgo(ageMs),
    title: `Last seen ${new Date(w.lastSeenAt).toLocaleString()}.`,
    bg: '#F1F5F9',
    fg: '#475569',
  };
}

function formatAgo(ms: number): string {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  return `${mo}mo ago`;
}

function CreateWorkerForm({ onClose }: { onClose: () => void }) {
  const create = useCreateWorker();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+971');
  const [email, setEmail] = useState('');
  const [employmentType, setEmploymentType] = useState<(typeof EMPLOYMENT_TYPES)[number]>('full_time');
  const [services, setServices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ name: string; phone: string; email: string; credsEmailed?: boolean } | null>(null);

  function toggleService(s: string) {
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body = workerCreateSchema.parse({
        name,
        phone,
        email,
        employmentType,
        services,
        active: true,
      });
      const result = await create.mutateAsync(body);
      setCreated({ name: result.name, phone: result.phone, email: result.email, credsEmailed: result.credsEmailed });
    } catch (err) {
      setError(err instanceof z.ZodError ? err.issues.map((i) => i.message).join(', ') : err instanceof Error ? err.message : String(err));
    }
  }

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <div
          className="rounded-lg border-l-4 bg-green-50 p-4 text-sm"
          style={{ borderColor: BRAND.success }}
        >
          <p className="font-semibold" style={{ color: BRAND.success }}>
            ? Worker created
          </p>
          <p className="mt-1 text-stone-700">
            {created.credsEmailed
              ? <>Cognito has emailed the temporary password to <strong>{created.email}</strong>. The worker can sign in at <a className="underline" href="https://app.fixitninjaz.com" target="_blank" rel="noreferrer">app.fixitninjaz.com</a> and will be required to set a new password.</>
              : <>This email was already registered � the existing Cognito user has been re-linked. No new email was sent.</>}
          </p>
        </div>
        <dl className="grid grid-cols-3 gap-y-2 text-sm">
          <dt className="text-stone-500">Name</dt>
          <dd className="col-span-2">{created.name}</dd>
          <dt className="text-stone-500">Email</dt>
          <dd className="col-span-2 font-mono break-all">{created.email}</dd>
          <dt className="text-stone-500">Phone</dt>
          <dd className="col-span-2 font-mono">{created.phone}</dd>
        </dl>
        <div className="flex justify-end">
          <Button type="button" onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {error && (
        <div
          role="alert"
          className="rounded border px-3 py-2 text-xs"
          style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
        >
          {error}
        </div>
      )}
      <Field label="Name">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Phone (UAE +971…)">
          <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </Field>
        <Field label="Email (required � used for sign-in)" hint="Worker signs in with this email + temp password.">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
      </div>
      <Field label="Employment type">
        <select
          className={inputClass}
          value={employmentType}
          onChange={(e) => setEmploymentType(e.target.value as (typeof EMPLOYMENT_TYPES)[number])}
        >
          {EMPLOYMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace('_', ' ')}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Services" hint="Pick at least one.">
        <div className="flex flex-wrap gap-2">
          {SERVICE_TYPES.map((s) => {
            const on = services.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleService(s)}
                className="rounded-full border px-3 py-1 text-xs"
                style={{
                  borderColor: on ? BRAND.primary : BRAND.border,
                  backgroundColor: on ? BRAND.primary : 'white',
                  color: on ? 'white' : BRAND.text,
                }}
              >
                {SERVICE_LABELS[s].en}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Create worker'}
        </Button>
      </div>
    </form>
  );
}
